import type { ChartConfig, OptionColor, PropertyDef, RowData } from './db-types';
import type { ValueOf } from './filter';
import { groupRows, type GroupOptions } from './group';

/** Chart view data (P-959). */
export interface ChartPoint {
  key: string;
  label: string;
  value: number;
  color?: OptionColor;
}

export interface ChartData {
  /** One per group, in the chart's order (none for a number). */
  points: ChartPoint[];
  /** The measure over all rows (a number chart shows it). */
  total: number;
}

/** A new chart view: bars counting rows per status, select, multi-select, person or date. */
export function defaultChart(props: PropertyDef[]): ChartConfig {
  const by = (['status', 'select', 'multi_select', 'person', 'date'] as const).map((t) => props.find((p) => p.type === t)).find(Boolean);
  return { type: 'bar', groupBy: by?.id ?? null, measure: { fn: 'count' }, sort: 'key', hideEmpty: false };
}

/** Count of rows, or a calculation of a number property over them (empty values left out). */
export function measureRows(rows: RowData[], m: ChartConfig['measure'], props: PropertyDef[], valueOf: ValueOf): number {
  if (m.fn === 'count') return rows.length;
  const prop = props.find((p) => p.id === m.propId);
  if (!prop) return 0;
  const nums = rows.map((r) => valueOf(r, prop)).filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (!nums.length) return 0;
  switch (m.fn) {
    case 'sum':
      return nums.reduce((a, b) => a + b, 0);
    case 'average':
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min':
      return Math.min(...nums);
    case 'max':
      return Math.max(...nums);
    case 'median': {
      const s = [...nums].sort((a, b) => a - b);
      const mid = Math.floor(s.length / 2);
      return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
    }
  }
}

export function chartData(rows: RowData[], chart: ChartConfig, props: PropertyDef[], valueOf: ValueOf, opts: GroupOptions = {}): ChartData {
  const total = measureRows(rows, chart.measure, props, valueOf);
  const prop = chart.groupBy ? props.find((p) => p.id === chart.groupBy) : undefined;
  if (chart.type === 'number' || !prop) return { points: [], total };
  const dateBy = chart.dateBy ?? (prop.type === 'date' || prop.type === 'created_time' || prop.type === 'last_edited_time' ? 'month' : undefined);
  let groups = groupRows(rows, { propId: prop.id, ...(dateBy ? { dateBy } : {}) }, prop, valueOf, opts);
  if (chart.hideEmpty) groups = groups.filter((g) => g.key !== '' && g.rows.length > 0);
  let points: ChartPoint[] = groups.map((g) => ({ key: g.key, label: g.label, value: measureRows(g.rows, chart.measure, props, valueOf), ...(g.color ? { color: g.color } : {}) }));
  if (chart.sort === 'value-desc') points = [...points].sort((a, b) => b.value - a.value);
  else if (chart.sort === 'value-asc') points = [...points].sort((a, b) => a.value - b.value);
  return { points, total };
}
